import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useSaveProblem } from "../../hooks/useProblems";
import MDEditor from "@uiw/react-md-editor";
import rehypeSanitize from "rehype-sanitize";

const AddProblemModal = ({ isOpen, onClose, initialData }) => {
  const { mutate: saveProblem, isPending } = useSaveProblem();
  const isEditMode = !!initialData;

  const [formData, setFormData] = useState({
    title: "",
    url: "",
    difficulty: "1",
    status: false,
    topic: [],
    note: "",
  });

  const [topicInput, setTopicInput] = useState("");

  // Populate data if we are editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        _id: initialData._id,
        title: initialData.title || "",
        url: initialData.url || "",
        difficulty: initialData.difficulty || "1",
        status: initialData.status || false,
        topic: initialData.topic || [],
        note: initialData.note || "",
      });
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleAddTopic = (e) => {
    if (e.key === "Enter" && topicInput.trim() !== "") {
      e.preventDefault();
      if (!formData.topic.includes(topicInput.trim())) {
        setFormData({ ...formData, topic: [...formData.topic, topicInput.trim()] });
      }
      setTopicInput("");
    }
  };

  const handleRemoveTopic = (topicToRemove) => {
    setFormData({
      ...formData,
      topic: formData.topic.filter((t) => t !== topicToRemove),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveProblem(formData, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <h2 className="text-lg font-semibold text-slate-800">
            {isEditMode ? "Edit Problem" : "Add New Problem"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              required
              placeholder="e.g., Two Sum"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">URL</label>
            <input
              type="url"
              placeholder="https://leetcode.com/problems/..."
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Difficulty <span className="text-red-500">*</span></label>
              <div className="flex gap-3">
                {["1", "2", "3"].map((level, i) => (
                  <label key={level} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="difficulty"
                      value={level}
                      checked={formData.difficulty === level}
                      onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-slate-300"
                    />
                    <span className="text-sm text-slate-600">
                      {i === 0 ? "Easy" : i === 1 ? "Medium" : "Hard"}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {isEditMode && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.status === false}
                      onChange={() => setFormData({ ...formData, status: false })}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-slate-300"
                    />
                    <span className="text-sm text-slate-600">Unsolved</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.status === true}
                      onChange={() => setFormData({ ...formData, status: true })}
                      className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 border-slate-300"
                    />
                    <span className="text-sm text-slate-600">Solved</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Topics</label>
            <div className="p-2 border border-slate-200 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white flex flex-wrap gap-2 transition-all">
              {formData.topic.map((t) => (
                <span key={t} className="flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-1 rounded-md text-sm">
                  {t}
                  <button type="button" onClick={() => handleRemoveTopic(t)} className="text-slate-400 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                placeholder="Type and press Enter..."
                value={topicInput}
                onChange={(e) => setTopicInput(e.target.value)}
                onKeyDown={handleAddTopic}
                className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            {/* Replace the old textarea block with this: */}
            <div className="pb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Notes & Code (Markdown Supported)</label>
              {/* data-color-mode forces it to match your light theme */}
              <div data-color-mode="light" className="rounded-lg overflow-hidden border border-slate-200">
                <MDEditor
                  value={formData.note}
                  onChange={(val) => setFormData({ ...formData, note: val || "" })}
                  previewOptions={{
                    rehypePlugins: [[rehypeSanitize]],
                  }}
                  height={300}
                  className="!border-none"
                />
              </div>
            </div>
          </div>

        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
          >
            {isPending ? "Saving..." : isEditMode ? "Save Changes" : "Add Problem"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddProblemModal;